/**
 * Account Creation Logic
 *
 * Functions for creating accounts and generating IDs.
 * Part of the accounts domain (split from accountHelpers.ts).
 */

import {
  Account,
  LearnerAccount,
  ParentAccount,
  TeacherAccount,
  AdminAccount,
  AccountsState,
  AccountRole,
} from '../types'

/**
 * Generate a simple UUID v4
 */
export function generateAccountId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Create a default admin account.
 * Used by ensureAdminAccount() as a safety-net migration.
 */
export function createDefaultAdminAccount(): AdminAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'admin',
    displayName: 'Admin',
    createdAt: now,
    lastActive: now,
  }
}

/**
 * Ensure an admin account exists in the accounts state.
 * Used as a post-load migration for states created before admin was added.
 */
export function ensureAdminAccount(
  accountsState: AccountsState,
): AccountsState {
  const hasAdmin = Object.values(accountsState.accountsById).some(
    (account) => account.role === 'admin',
  )
  if (hasAdmin) return accountsState

  const admin = createDefaultAdminAccount()
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [admin.id]: admin,
    },
  }
}

/**
 * Create a new account and add it to the accounts state.
 * Pure function — returns updated AccountsState and the new account ID.
 */
export function createAccountInState(
  accountsState: AccountsState,
  role: AccountRole,
  details: {
    displayName: string
    email?: string
    schoolName?: string
    grade?: number
    passwordHash?: string
    username?: string
    linkCode?: string
  },
  providedId?: string,
): { accounts: AccountsState; newAccountId: string } {
  if (!details || typeof details.displayName !== 'string') {
    throw new Error(
      `createAccountInState: invalid details argument (got ${typeof details}). ` +
        `Expected { displayName: string, ... }. Role: ${role}, providedId: ${providedId}`,
    )
  }
  const newId = providedId || generateAccountId()
  const now = Date.now()

  const base = {
    id: newId,
    role,
    displayName: details.displayName,
    email: details.email,
    createdAt: now,
    lastActive: now,
    passwordHash: details.passwordHash,
  }

  let newAccount: Account
  switch (role) {
    case 'teacher':
      newAccount = {
        ...base,
        role: 'teacher',
        schoolName: details.schoolName,
      } as TeacherAccount
      break
    case 'learner':
      newAccount = {
        ...base,
        role: 'learner',
        username:
          details.username ||
          details.displayName.toLowerCase().replace(/\s+/g, ''),
        grade: details.grade,
        linkCode: details.linkCode,
      } as LearnerAccount
      break
    case 'parent':
      newAccount = { ...base, role: 'parent' } as ParentAccount
      break
    case 'admin':
      newAccount = { ...base, role: 'admin' } as AdminAccount
      break
  }

  return {
    accounts: {
      ...accountsState,
      accountsById: {
        ...accountsState.accountsById,
        [newId]: newAccount,
      },
    },
    newAccountId: newId,
  }
}
